'use client';

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { PipelineRun } from '@/lib/types';
import { applyNodeResults } from '@/lib/pipeline/engine';
import { usePipelineStore } from '@/lib/pipeline/store';

function fmtTime(s: string): string {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
}

function statusBadge(s: string): string {
  if (s === 'completed') return 'badge-success';
  if (s === 'failed') return 'badge-error';
  if (s === 'processing') return 'badge-warning';
  return 'badge-muted';
}

/** Last image produced by the run (closest to the final output), if any. */
function thumbUrl(run: PipelineRun): string | null {
  let url: string | null = null;
  for (const r of Object.values(run.node_results ?? {})) {
    if (r.image) url = api.getImageUrl(r.image);
    else if (r.filename) url = api.getImageUrl(r.filename);
  }
  return url;
}

export default function RunHistoryPanel({ onCollapse }: { onCollapse?: () => void }) {
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  // Run output files are reaped after 24h; remember which thumbs 404'd so we
  // drop them cleanly instead of showing a broken-image icon on every refresh.
  const [brokenThumbs, setBrokenThumbs] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    try {
      setRuns(await api.listPipelineRuns(30, 0));
    } catch {
      /* best-effort */
    }
  }, []);

  useEffect(() => {
    refresh();
    const i = setInterval(refresh, 5000); // keep active runs fresh
    return () => clearInterval(i);
  }, [refresh]);

  const apply = (run: PipelineRun) => {
    applyNodeResults(run.node_results ?? {}, usePipelineStore.getState().setRuntime);
  };

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider">
          Run history
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="text-[10px] text-[var(--text-muted)] hover:text-[var(--accent)] transition-subtle"
          >
            Refresh
          </button>
          {onCollapse && (
            <button
              onClick={onCollapse}
              title="Collapse panel"
              className="text-sm leading-none text-[var(--text-muted)] hover:text-[var(--accent)] transition-subtle"
            >
              ›
            </button>
          )}
        </div>
      </div>

      {runs.length === 0 ? (
        <div className="text-xs text-[var(--text-muted)]">No server runs yet</div>
      ) : (
        <div className="space-y-2">
          {runs.map((r) => {
            const nodes = Object.values(r.node_results ?? {});
            const done = nodes.filter((n) => n.status === 'done').length;
            const thumb = thumbUrl(r);
            const showThumb = thumb && !brokenThumbs.has(thumb);
            return (
              <button
                key={r.id}
                onClick={() => apply(r)}
                title="Show this run's results on the canvas"
                className="w-full text-left p-2 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] hover:border-[var(--accent)] transition-subtle cursor-pointer"
              >
                <div className="flex gap-2">
                  {showThumb && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={thumb}
                      alt=""
                      loading="lazy"
                      onError={() => setBrokenThumbs((s) => new Set(s).add(thumb))}
                      className="w-10 h-10 rounded object-cover bg-[var(--bg-base)] shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium">Run #{r.id}</span>
                      <span className={`badge ${statusBadge(r.status)}`}>{r.status}</span>
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">
                      {done}/{nodes.length} nodes · {fmtTime(r.created_at)}
                    </div>
                    {r.error && (
                      <div className="text-[10px] text-[var(--error)] mt-1 line-clamp-2">
                        {r.error}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
